import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import {
  addEntity,
  removeEntity,
  setAllEntities,
  updateEntity,
  withEntities,
} from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  LaravelApi,
  setError,
  setFulfilled,
  setPending,
  withRequestStatus,
} from '@fsf/util';
import { concatMap, from, map, pipe, tap } from 'rxjs';
import {
  candidateSiteSchema,
  CandidateSite,
} from '../models/candidate-site.model';

/** Fields supplied at creation; the server stamps id/createdAt/organizationId. */
export type NewCandidateSite = Omit<
  CandidateSite,
  'id' | 'createdAt' | 'organizationId'
>;

/** A homomorphic mapped type rather than the interface, so it satisfies `Record<string, unknown>`. */
export type CandidateSiteApiModel = {
  [K in keyof CandidateSite]: CandidateSite[K];
};

/**
 * The store is root-provided and outlives its consumers, so a plain "last saved site" would
 * re-confirm an old save on every re-entry. A consumer captures the `seq` present at
 * registration and reacts only to a later one.
 */
export type LastSavedSite = { site: CandidateSite; seq: number };

/**
 * `removeMany` bumps `seq` and resets to `pending` per call, then lands on `done` or `error`.
 * Consumers reconcile selection off this terminal state plus the surviving `sites`.
 */
export type BulkRemoveStatus = {
  status: 'idle' | 'pending' | 'done' | 'error';
  seq: number;
};

type CandidateSiteState = {
  selectedSite: CandidateSite | null;
  lastSaved: LastSavedSite | null;
  bulkRemove: BulkRemoveStatus;
  loaded: boolean;
};

const initialState: CandidateSiteState = {
  selectedSite: null,
  lastSaved: null,
  bulkRemove: { status: 'idle', seq: 0 },
  loaded: false,
};

/**
 * Mutations are NOT optimistic: entity state is patched only inside each `rxMethod`'s success
 * handler, so a failed HTTP mutation leaves the collection untouched.
 *
 * Caller-facing results surface as state (`lastSaved`, `bulkRemove`) rather than returned
 * observables, so consumers depend only on signals. Consumers resolving a site by id gate on
 * the `loaded` latch, otherwise a deep-link or refresh flashes not-found.
 */
export const CandidateSiteStore = signalStore(
  { providedIn: 'root' },
  withEntities<CandidateSite>(),
  withRequestStatus,
  withState(initialState),
  withComputed(({ entities }) => ({
    // The company's saved sites; display order is applied by consumers.
    sites: entities,
  })),
  withMethods((store) => {
    const api = inject<LaravelApi<CandidateSiteApiModel>>(LaravelApi);
    return {
      readMany: rxMethod<void>(
        pipe(
          tap(() => patchState(store, setPending())),
          concatMap(() =>
            api.readMany().pipe(
              // Parse at the API boundary: a schema violation throws here and lands in
              // tapResponse's error handler, instead of inserting a corrupt entity.
              map((result) => candidateSiteSchema.array().parse(result.data)),
              tapResponse({
                next: (sites) =>
                  patchState(store, setAllEntities(sites), setFulfilled()),
                error: (error: unknown) => patchState(store, setError(error)),
                // One-way latch: the first fetch flips it either way, refreshes leave it true.
                finalize: () => patchState(store, { loaded: true }),
              }),
            ),
          ),
        ),
      ),
      addOne: rxMethod<NewCandidateSite>(
        pipe(
          tap(() => patchState(store, setPending())),
          concatMap((input) =>
            api.addOne(input as Partial<CandidateSiteApiModel>).pipe(
              map((result) => candidateSiteSchema.parse(result.data)),
              tapResponse({
                // Insert only after the server confirms, so a failed POST changes nothing.
                next: (site) =>
                  patchState(
                    store,
                    addEntity(site),
                    (state) => ({
                      lastSaved: { site, seq: (state.lastSaved?.seq ?? 0) + 1 },
                    }),
                    setFulfilled(),
                  ),
                error: (error: unknown) => patchState(store, setError(error)),
              }),
            ),
          ),
        ),
      ),
      updateOne: rxMethod<{
        id: string;
        patch: Partial<
          Pick<
            CandidateSite,
            'label' | 'notes' | 'destinationShare' | 'onRouteShare'
          >
        >;
      }>(
        pipe(
          tap(() => patchState(store, setPending())),
          concatMap(({ id, patch }) =>
            api.updateOne(id, patch as Partial<CandidateSiteApiModel>).pipe(
              map((result) => candidateSiteSchema.parse(result.data)),
              tapResponse({
                next: (updated) =>
                  patchState(
                    store,
                    updateEntity({ id, changes: updated }),
                    setFulfilled(),
                  ),
                error: (error: unknown) => patchState(store, setError(error)),
              }),
            ),
          ),
        ),
      ),
      removeOne: rxMethod<string>(
        pipe(
          tap(() => patchState(store, setPending())),
          concatMap((id) =>
            api.removeOne(id).pipe(
              tapResponse({
                // Drop only after the server confirms; a failed DELETE keeps the site.
                next: () => patchState(store, removeEntity(id), setFulfilled()),
                error: (error: unknown) => patchState(store, setError(error)),
              }),
            ),
          ),
        ),
      ),
      removeMany: rxMethod<readonly string[]>(
        pipe(
          concatMap((ids) => {
            // Capture THIS call's seq: `concatMap` queues overlapping calls, so re-reading
            // live state in the terminal handler would stamp a later call's token and the
            // consumer's seq-gated reconcile would misattribute the result.
            let mySeq = 0;
            patchState(store, (state) => {
              mySeq = state.bulkRemove.seq + 1;
              return { bulkRemove: { status: 'pending' as const, seq: mySeq } };
            });
            // A mid-sequence failure aborts the remainder, leaving the deleted prefix
            // dropped and the rest listed.
            return from(ids).pipe(
              concatMap((id) =>
                api
                  .removeOne(id)
                  .pipe(tap(() => patchState(store, removeEntity(id)))),
              ),
              // On the OUTER sequence, so `error` also recovers the rxMethod for later calls.
              tapResponse({
                next: () => undefined,
                error: () =>
                  patchState(store, {
                    bulkRemove: { status: 'error' as const, seq: mySeq },
                  }),
                complete: () =>
                  patchState(store, {
                    bulkRemove: { status: 'done' as const, seq: mySeq },
                  }),
              }),
            );
          }),
        ),
      ),
      select(site: CandidateSite | null): void {
        patchState(store, { selectedSite: site });
      },
      selectById(id: string): void {
        patchState(store, {
          selectedSite: store.entities().find((site) => site.id === id) ?? null,
        });
      },
    };
  }),
  withHooks({
    onInit(store) {
      store.readMany();
    },
  }),
);
